"use client";

import { type ComponentPropsWithoutRef, useId } from "react";
import { twMerge } from "tailwind-merge";
import { useIsClient } from "usehooks-ts";
import styles from "./AutoPostBolt.module.css";

// The 24px Heroicons outline bolt, shared by the base glyph and its wave clip.
const BOLT_PATH = "m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z";

type AutoPostBoltProps = ComponentPropsWithoutRef<"svg"> & {
  active: boolean;
  waiting?: boolean;
  loading?: boolean;
};

/** A steady active bolt with the processing loader's rising shade while waiting. */
export default function AutoPostBolt({ active, waiting = false, loading = false, className, ...props }: AutoPostBoltProps) {
  const id = useId();
  const isClient = useIsClient();
  const clipId = `${id}-clip`;
  const waveId = `${id}-wave`;
  const showWave = active && waiting && !loading;

  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
      focusable="false"
      data-slot="icon"
      data-auto-post-waiting={showWave || undefined}
      className={twMerge(
        active ? "fill-primary stroke-primary drop-shadow-[0_0_6px_rgb(59_130_246/0.9)]" : "stroke-slate-400",
        loading && "motion-safe:animate-pulse",
        className,
      )}
    >
      <path d={BOLT_PATH} strokeLinecap="round" strokeLinejoin="round" />
      {/* Popover's server/client trees differ, so insert ID-linked paint only after hydration. */}
      {showWave && isClient && (
        <>
          <defs>
            <clipPath id={clipId}>
              <path d={BOLT_PATH} />
            </clipPath>
            <linearGradient id={waveId} x1="0" y1="0" x2="0" y2="24" gradientUnits="userSpaceOnUse">
              <stop offset="13%" stopColor="white" stopOpacity="0" />
              <stop offset="38%" stopColor="white" stopOpacity="0.1" />
              <stop offset="48%" stopColor="white" stopOpacity="0.6" />
              <stop offset="50%" stopColor="white" />
              <stop offset="52%" stopColor="white" stopOpacity="0.6" />
              <stop offset="62%" stopColor="white" stopOpacity="0.1" />
              <stop offset="87%" stopColor="white" stopOpacity="0" />
            </linearGradient>
          </defs>
          <g clipPath={`url(#${clipId})`} stroke="none" opacity="0.55">
            <rect className={styles.wave} width="24" height="24" fill={`url(#${waveId})`} />
          </g>
        </>
      )}
    </svg>
  );
}
